import { useCallback } from 'react'
import { debounce } from 'lodash-es'
import { NonCachedSearchType } from 'interfaces'
import { StoreInstances } from 'src/store/StoreInstancesContainer'
import { Filter, type Filters } from 'src/store/models/SearchModels'
import { useResultsStore } from 'results'
import { useSearchStore } from 'src/store/search/SearchStore'
import { FilterGroupName, LineTypes } from 'results/constants'
import { ListItem } from './interfaces'
import type { AppliedFilterChipDescriptor } from './types'

const isAllLinesSelection = (
  filterName: string,
  selectedOptions: ListItem[]
): boolean =>
  filterName === FilterGroupName.LINES &&
  selectedOptions.length > 0 &&
  selectedOptions[0].value === LineTypes.ALL_LINES

const saveStockFilterPreference = (newFilters: Filter[]): void => {
  const stockFilter = newFilters.find(
    (f) => f.groupName === FilterGroupName.STOCK
  )
  if (stockFilter?.items?.[0]) {
    StoreInstances?.userStore?.saveStockFilterPreference(stockFilter.items[0])
  }
}

export const useSearchFilterActions = (): {
  applyFilterChange: (
    filterName: string,
    selectedOptions: ListItem[],
    clearExisting?: boolean
  ) => void
  clearAllFilters: () => void
  removeChip: (chip: AppliedFilterChipDescriptor) => void
  resetLinesSelection: () => void
} => {
  const resultsStore = useResultsStore()
  const searchStore = useSearchStore()

  const {
    filters: selectedFilters,
    setFilters: setSelectedFilters,
    firstSelectedFilterType,
    clearResults,
    lastNonCachedSearch,
    setIsLoading,
    applyInterchangeSearchFilters,
    searchByGfxPartBin,
    searchByPartBin,
    setFirstSelectedFilterType,
    setSelectedLinesManufacturers,
    selectedLinesManufacturers,
  } = resultsStore

  const { gfxLookupMade, lastTextSearched } = searchStore

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchByPartBinDebounced = useCallback(
    debounce((filterName: string) => {
      searchByPartBin(undefined, filterName)
    }, 1000),
    [searchByPartBin]
  )

  const updateFirstSelectedFilterType = useCallback(
    (filterName: string, newFilters: Filter[]): void => {
      const isBrandsOrPartType =
        filterName === FilterGroupName.BRANDS ||
        filterName === FilterGroupName.PART_TYPE

      if (isBrandsOrPartType && firstSelectedFilterType === undefined) {
        setFirstSelectedFilterType(filterName)
        // Safe early return: if firstSelectedFilterType is undefined here,
        // the condition below (firstSelectedFilterType && ...) is always false
        return
      }

      if (
        firstSelectedFilterType &&
        !newFilters.find((f) => f.groupName === firstSelectedFilterType)
      ) {
        const alt =
          firstSelectedFilterType === FilterGroupName.BRANDS
            ? FilterGroupName.PART_TYPE
            : FilterGroupName.BRANDS
        setFirstSelectedFilterType(
          newFilters.find((f) => f.groupName === alt) ? alt : undefined
        )
      }
    },
    [firstSelectedFilterType, setFirstSelectedFilterType]
  )

  const triggerSearch = useCallback(
    (filterName: string, newFilters: Filters): void => {
      if (
        lastNonCachedSearch === NonCachedSearchType.searchByText &&
        filterName !== FilterGroupName.STOCK
      ) {
        resultsStore.filterAstPartSearch(lastTextSearched, newFilters)
        return
      }

      if (lastNonCachedSearch === NonCachedSearchType.searchByInterchange) {
        applyInterchangeSearchFilters()
        return
      }

      if (gfxLookupMade) {
        searchByGfxPartBin()
        return
      }

      searchByPartBinDebounced(filterName)
    },
    [
      applyInterchangeSearchFilters,
      gfxLookupMade,
      lastNonCachedSearch,
      lastTextSearched,
      resultsStore,
      searchByGfxPartBin,
      searchByPartBinDebounced,
    ]
  )

  const applyFilterChange = useCallback(
    (
      filterName: string,
      selectedOptions: ListItem[],
      clearExisting = false
    ): void => {
      const newFilters = clearExisting
        ? []
        : selectedFilters.filter((i) => i.groupName !== filterName)
      if (selectedOptions.length > 0) {
        newFilters.push({
          groupName: filterName,
          items: selectedOptions.map((option) => option.value.toString()),
        })
      }
      setIsLoading(true)

      if (
        filterName !== FilterGroupName.STOCK &&
        !isAllLinesSelection(filterName, selectedOptions)
      ) {
        clearResults()
      }

      if (filterName === FilterGroupName.STOCK) {
        saveStockFilterPreference(newFilters)
      }

      updateFirstSelectedFilterType(filterName, newFilters)
      setSelectedFilters(newFilters)

      if (isAllLinesSelection(filterName, selectedOptions)) {
        setIsLoading(false)
        return
      }

      triggerSearch(filterName, newFilters)
    },
    [
      clearResults,
      selectedFilters,
      setIsLoading,
      setSelectedFilters,
      triggerSearch,
      updateFirstSelectedFilterType,
    ]
  )

  const resetLinesSelection = useCallback((): void => {
    applyFilterChange(FilterGroupName.LINES, [])
  }, [applyFilterChange])

  const clearAllFilters = useCallback((): void => {
    setSelectedLinesManufacturers([])
    setFirstSelectedFilterType(undefined)
    setSelectedFilters([])
    clearResults()
    setIsLoading(true)

    if (lastNonCachedSearch === NonCachedSearchType.searchByText) {
      resultsStore.filterAstPartSearch(lastTextSearched, [])
      return
    }

    if (lastNonCachedSearch === NonCachedSearchType.searchByInterchange) {
      applyInterchangeSearchFilters()
      return
    }

    if (gfxLookupMade) {
      searchByGfxPartBin(0)
      return
    }

    searchByPartBin(0, undefined, true)
  }, [
    applyInterchangeSearchFilters,
    clearResults,
    gfxLookupMade,
    lastNonCachedSearch,
    lastTextSearched,
    resultsStore,
    searchByGfxPartBin,
    searchByPartBin,
    setFirstSelectedFilterType,
    setIsLoading,
    setSelectedFilters,
    setSelectedLinesManufacturers,
  ])

  const removeChip = useCallback(
    (chip: AppliedFilterChipDescriptor): void => {
      if (chip.kind === 'manufacturer' && chip.value) {
        const nextManufacturers = selectedLinesManufacturers.filter(
          (m) => m !== chip.value
        )
        setSelectedLinesManufacturers(nextManufacturers)
        if (nextManufacturers.length === 0) {
          resetLinesSelection()
        }
        clearResults()
        setIsLoading(true)
        if (gfxLookupMade) {
          searchByGfxPartBin()
        } else {
          searchByPartBin(undefined, FilterGroupName.LINES)
        }
        return
      }

      if (chip.kind === 'facet' && chip.groupName && chip.value) {
        const group = selectedFilters.find(
          (f) => f.groupName === chip.groupName
        )
        if (!group) {
          return
        }
        const remainingItems = group.items.filter((item) => item !== chip.value)
        const remainingOptions: ListItem[] = remainingItems.map((item) => ({
          value: item,
          label: item,
          active: true,
        }))
        applyFilterChange(chip.groupName, remainingOptions)
      }
    },
    [
      applyFilterChange,
      clearResults,
      gfxLookupMade,
      resetLinesSelection,
      searchByGfxPartBin,
      searchByPartBin,
      selectedFilters,
      selectedLinesManufacturers,
      setIsLoading,
      setSelectedLinesManufacturers,
    ]
  )

  return {
    applyFilterChange,
    clearAllFilters,
    removeChip,
    resetLinesSelection,
  }
}
